import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { withUids, stripUids } from '../lib/listKeys';
import { LoyaltyProgrammeSettings } from '../components/loyalty/LoyaltyProgrammeSettings';
import { LoyaltyMembers } from '../components/loyalty/LoyaltyMembers';
import { LoyaltyDialogs } from '../components/loyalty/LoyaltyDialogs';
import { fmtNumber } from '../lib/localeUtils';

export default function LoyaltyPage() {
  const [tab, setTab] = useState('programme');
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [adjustAccount, setAdjustAccount] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/loyalty/settings');
      setSettings({ ...data, tiers: withUids(data?.tiers) });
    } catch { toast.error('Could not load loyalty settings'); }
  }, []);
  const loadAccounts = useCallback(async (query = search) => {
    setLoadingAccounts(true);
    try {
      const { data } = await api.get('/loyalty/accounts', { params: { search: query, page: 1, limit: 30 } });
      setAccounts(data.accounts || []); setTotal(data.total || 0);
    } catch { toast.error('Could not load loyalty members'); }
    finally { setLoadingAccounts(false); }
  }, [search]);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { const timer = setTimeout(() => loadAccounts(search), 300); return () => clearTimeout(timer); }, [search, loadAccounts]);

  const summary = useMemo(() => ({ totalBal: accounts.reduce((sum, account) => sum + (account.balance || 0), 0), totalLifetime: accounts.reduce((sum, account) => sum + (account.lifetime_points || 0), 0) }), [accounts]);
  const updateSettings = (patch) => setSettings((current) => ({ ...current, ...patch }));
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put('/loyalty/settings', { ...settings, tiers: stripUids(settings.tiers) });
      setSettings({ ...data, tiers: withUids(data?.tiers) }); toast.success('Loyalty settings saved');
    } catch (error) { toast.error(error?.response?.data?.detail || 'Save failed'); }
    finally { setSavingSettings(false); }
  };
  const openDetail = async (account) => {
    try { const { data } = await api.get(`/loyalty/accounts/${account.loyalty_id}`); setDetail(data); setDetailOpen(true); }
    catch { toast.error('Could not open member detail'); }
  };
  const downloadCard = async (account) => {
    try {
      const response = await api.get(`/loyalty/accounts/${account.loyalty_id}/card.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data); const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `loyalty-card-${account.member_code || account.loyalty_id}.pdf`;
      document.body.appendChild(anchor); anchor.click(); document.body.removeChild(anchor); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { toast.error(error?.response?.data?.detail || 'Card download failed'); }
  };
  const submitAdjustment = async ({ points, reason }) => {
    try {
      await api.post('/loyalty/adjust', { loyalty_id: adjustAccount.loyalty_id, points, reason });
      toast.success('Adjustment posted'); setAdjustAccount(null); loadAccounts();
      if (detailOpen && detail?.account?.loyalty_id === adjustAccount.loyalty_id) openDetail(adjustAccount);
    } catch (error) { toast.error(error?.response?.data?.detail || 'Adjustment failed'); }
  };
  if (!settings) return <div className="min-h-[40vh] flex items-center justify-center" data-testid="loyalty-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return <div className="space-y-6" data-testid="loyalty-page"><div className="flex items-center justify-between"><div><h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Sparkles className="w-7 h-7 text-primary" /> Loyalty Programme</h1><p className="text-sm text-muted-foreground">Reward regulars — configure rules & manage member balances.</p></div><Badge variant={settings.enabled ? 'default' : 'outline'} className="text-xs" data-testid="loyalty-enabled-badge">{settings.enabled ? 'Enabled' : 'Disabled'}</Badge></div><Tabs value={tab} onValueChange={setTab}><TabsList className="grid grid-cols-2 max-w-md"><TabsTrigger value="programme" data-testid="loyalty-tab-programme">Programme</TabsTrigger><TabsTrigger value="members" data-testid="loyalty-tab-members">Members ({fmtNumber(total)})</TabsTrigger></TabsList><TabsContent value="programme" className="space-y-4 mt-4"><LoyaltyProgrammeSettings settings={settings} saving={savingSettings} onChange={updateSettings} onSave={saveSettings} /></TabsContent><TabsContent value="members" className="space-y-4 mt-4"><LoyaltyMembers accounts={accounts} search={search} onSearchChange={setSearch} summary={summary} loading={loadingAccounts} onView={openDetail} onAdjust={setAdjustAccount} onDownloadCard={downloadCard} /></TabsContent></Tabs><LoyaltyDialogs detail={detail} detailOpen={detailOpen} onDetailOpenChange={setDetailOpen} adjustAccount={adjustAccount} onAdjustOpenChange={(open) => !open && setAdjustAccount(null)} onSubmitAdjustment={submitAdjustment} onDownloadCard={downloadCard} /></div>;
}